import React, {useCallback, useState} from 'react';
import {toast} from 'react-toastify';
import ExperienceForm from './ExperienceForm';
import ExperienceList from './ExperienceList';
import {createCurriculumVitaeExperience, deleteCurriculumVitaeExperience, updateCurriculumVitaeExperience} from '../../../../api/curriculumVitaeApi';
import {COMPLETE_CURRICULUM_VITAE_EXPERIENCE_TOAST_MESSAGES, getErrorUpdateToastPropsBy, getSuccesToastPropsBy} from '../../../../service/toastService';
import {useDispatch} from 'react-redux';
import {showModal} from '../../../../redux/system/actions';
import ConfirmModal from '../../../common/modals/ConfirmModal';

const CONFIRM_MODAL_ID = 'confirmModal';

function ProfessionalExperience({curriculumVitae, setCurriculumVitae}) {
    const dispatch = useDispatch();
    const handleShowModal = useCallback((modalId) => dispatch(showModal(modalId)), [dispatch]);

    const [experiences, setExperiences] = useState(curriculumVitae.curriculumVitaeExperiences || []);
    const [editingExperience, setEditingExperience] = useState(null);
    const [experienceToDelete, setExperienceToDelete] = useState(null);

    const handleSaveExperience = async (experienceData) => {
        try {
            if (editingExperience) {
                const updatedExperience = await updateCurriculumVitaeExperience(experienceData);
                const updatedExperiences = experiences.map((exp) =>
                    exp.id === experienceData.id ? updatedExperience.data : exp
                );
                setExperiences(updatedExperiences);
                setCurriculumVitae((prevCv) => ({
                    ...prevCv,
                    curriculumVitaeExperiences: updatedExperiences,
                }));

                toast.success('Experience updated successfully!', getSuccesToastPropsBy(COMPLETE_CURRICULUM_VITAE_EXPERIENCE_TOAST_MESSAGES));
            } else {
                const newExperience = await createCurriculumVitaeExperience(experienceData);
                const updatedExperiences = [...experiences, newExperience.data];
                setExperiences(updatedExperiences);
                setCurriculumVitae((prevCv) => ({
                    ...prevCv,
                    curriculumVitaeExperiences: updatedExperiences,
                }));

                toast.success('Experience added successfully!', getSuccesToastPropsBy(COMPLETE_CURRICULUM_VITAE_EXPERIENCE_TOAST_MESSAGES));
            }

            setEditingExperience(null);
        } catch (error) {
            toast.error('Something went wrong! Please try again.', getErrorUpdateToastPropsBy(COMPLETE_CURRICULUM_VITAE_EXPERIENCE_TOAST_MESSAGES));
        }
    };

    const handleDeleteExperience = async () => {
        if (!experienceToDelete) return;
        try {
            await deleteCurriculumVitaeExperience(experienceToDelete);

            setExperiences((prevExperiences) => prevExperiences.filter((exp) => exp.id !== experienceToDelete));
            setCurriculumVitae((prevCv) => {
                const updatedExperiences = prevCv.curriculumVitaeExperiences.filter((exp) => exp.id !== experienceToDelete);
                return {
                    ...prevCv,
                    curriculumVitaeExperiences: updatedExperiences,
                };
            });
            toast.success('Experience deleted successfully!', getSuccesToastPropsBy(COMPLETE_CURRICULUM_VITAE_EXPERIENCE_TOAST_MESSAGES));
        } catch (error) {
            toast.error('Something went wrong! Please try again.', getErrorUpdateToastPropsBy(COMPLETE_CURRICULUM_VITAE_EXPERIENCE_TOAST_MESSAGES));
        }
    };

    const handleEditExperience = (experience) => {
        setEditingExperience(experience);
    };

    const confirmDeleteExperience = async (experienceId) => {
        setExperienceToDelete(experienceId);
        handleShowModal(CONFIRM_MODAL_ID);
    };

    return (
        <div>
            <h4 className="mb-4" style={{textDecoration: 'underline', textUnderlineOffset: '7px'}}>
                Professional Experience
            </h4>
            <ExperienceForm
                curriculumVitaeId={curriculumVitae.id}
                initialExperience={editingExperience}
                setEditingExperience={setEditingExperience}
                onSave={handleSaveExperience}
                status={curriculumVitae.status}
            />
            <ExperienceList experiences={experiences} onEdit={handleEditExperience} onDelete={confirmDeleteExperience}/>

            <ConfirmModal
                id={CONFIRM_MODAL_ID}
                title="Are you sure you want to delete this professional experience?"
                message="This will permanently remove the selected experience from your profile. Do you wish to proceed?"
                onConfirm={handleDeleteExperience}
            />
        </div>
    );
}

export default ProfessionalExperience;
